import { House as BaseHouse } from '@/types/admin';

export type ContractEventType =
  | 'created'
  | 'accepted_by_admin'
  | 'accepted_by_roommates'
  | 'accepted_by_house_manager'
  | 'application_cancelled_by_coloc'
  | 'application_rejected_by_admin'
  | 'application_rejected_by_notice_cancellation'
  | 'application_rejected_by_availability_date_change'
  | 'application_rejected_by_deadline'
  | 'application_rejected_by_active_contract'
  | 'application_rejected_by_room_rented'
  | 'application_rejected_by_vote'
  | 'application_rejected_by_unknown_reason';

export type RoomAssignmentStatus =
  | 'not_available'
  | 'searching_applicants'
  | 'awaiting_signature'
  | 'awaiting_deposit'
  | 'deposit_paid'
  | 'occupied'
  | 'rented';

export type RoomAssignmentUrgency = 'very_high' | 'high' | 'medium' | 'low';

export type RoomAssignmentUrgencyReason =
  | 'available_now'
  | 'available_soon'
  | 'summer_vacation'
  | 'winter_vacation';

export type RentalType = 'new' | 're-rental' | 'relaunch';

export type RoomApplicantUser = {
  id: string;
  name: string;
  birthday: Date;
  nationality: { country_code: string; country_name: string };
  languages: Array<{ code: string }>;
  work: {
    profession: string;
    company: string;
  };
  phone_number: {
    country_code: string;
    number: string;
  };
  email: string;
  intercom_user_id?: string;
  avatar_url?: string;
};

export type RoomApplicantVotes = {
  accepted: number;
  rejected: number;
  expected_votes: number;
};

export type RoomApplicantChat = { id: number; last_message_stamp: Date };

export const ApplicantReasonContractReason = {
  applied: 'applied',
  accepted_by_admin: 'accepted_by_admin',
  accepted_by_roommates: 'accepted_by_roommates',
  accepted_by_house_manager: 'accepted_by_house_manager',
  accepted_by_queue: 'accepted_by_queue',
  application_cancelled_by_coloc: 'application_cancelled_by_coloc',
  application_rejected_by_admin: 'application_rejected_by_admin',
  application_rejected_by_notice_cancellation: 'application_rejected_by_notice_cancellation',
  application_rejected_by_availability_date_change:
    'application_rejected_by_availability_date_change',
  application_rejected_by_deadline: 'application_rejected_by_deadline',
  application_rejected_by_active_contract: 'application_rejected_by_active_contract',
  application_rejected_by_room_rented: 'application_rejected_by_room_rented',
  application_rejected_by_vote: 'application_rejected_by_vote',
  application_rejected_by_unknown_reason: 'application_rejected_by_unknown_reason',
  queued_by_voting: 'queued_by_voting',
  queued_by_house_manager: 'queued_by_house_manager',
  queued_by_owner: 'queued_by_owner',
  queued_by_admin: 'queued_by_admin',
} as const;

export type ApplicantReasonContractReasonType = keyof typeof ApplicantReasonContractReason;

export type ApplicantsContractEvent = {
  created_at: Date;
  action:
    | 'applied'
    | 'queued'
    | 'accepted'
    | 'activated'
    | 'signed'
    | 'cancelled'
    | 'voted'
    | 'changed_tenancy'
    | 'charges_updated'
    | 'rent_indexed';
  reason: string;
  actor: {
    id: string;
    name: string;
    type: string;
  };
};

export type RoomApplicant = {
  user: RoomApplicantUser;
  contract: {
    id: number;
    votes: RoomApplicantVotes;
    status: 'applied' | 'cancelled';
    contract_event: ApplicantsContractEvent;
    move_in_date?: Date;
    motivation: string;
    created_at: Date;
    updated_at: Date;
  };
  security_deposit?: {
    awaiting_sepa_payment: Boolean;
  };
  chat: RoomApplicantChat;
};

export type ApplicantTenant = {
  id: string;
  name: string;
  nationality: string;
  phone_number: {
    country_code: string;
    number: string;
  };
  email: string;
  contract_id: string;
};

export type RoomAssignmentRoom = {
  id: string;
  room_number: number;
  house: {
    name: string;
  };
};

export type RoomAssignment = {
  id: number;
  discussion_thread: {
    messages_count: number;
    id: string | null;
  };
  urgency: {
    status: RoomAssignmentUrgency;
    reason?: RoomAssignmentUrgencyReason;
  };
  room: RoomAssignmentRoom;
  checkin_contract?: {
    security_deposit?: {
      awaiting_sepa_payment: Boolean;
    };
  };
  checkout_tenant?: ApplicantTenant;
  checkin_tenant?: ApplicantTenant;
  current_tenant?: ApplicantTenant;
  assignment_status: {
    status: RoomAssignmentStatus;
    last_update: Date;
  };
  availability_date?: Date;
  end_of_notice_period?: Date;
  notice_of_termination_received_at?: Date;
  applications: Array<RoomApplicant>;
  new_room: boolean;
  rental_type: RentalType;
};

export type PropertyManager = {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url: string;
};

export interface Contract {
  id: number;
  created_at: string;
  status: string;
  colocation_id: number;
  room_number: number;
  move_in: string;
  move_out: string | null;
  full_rent: {
    cents: number;
    currency_iso: string;
  };
  coloc: {
    id: number;
    name: string;
  };
  colocation: {
    id: number;
    name: string;
    property_manager: {
      name: string;
    };
  };
  accepting_votes_ratio?: number;
  active_votes: Array<{
    id: number;
    status: string;
    voter: {
      id: number;
      first_name: string;
      last_name: string;
    };
  }>;
  residency_registration_status?: string;
  sepa_status?: string;
  discussion_thread?: {
    messages_count: number;
    id: string | null;
  };
  discounts?: Discount[];
}

export type Discount = {
  id: number;
  amount_cents: number;
  date: string;
  note?: string;
  created_at: string;
  percentage?: number;
  period?: 'recurring' | 'next_month' | 'this_month';
  start_date?: string;
  end_date?: string;
  discount_category?: string;
};

export const ResidencyStatus = {
  '0': 'To Do',
  '1': 'Pending',
  '2': 'Overdue',
  '3': 'Done',
} as const;

export enum SepaStatus {
  prepared = 'prepared',
  signed = 'signed',
  not_signed = 'No SEPA',
}

export enum ResidencyRegistrationStatus {
  TO_DO = 'to_do',
  PENDING = 'pending',
  OVERDUE = 'overdue',
  DONE = 'done',
}

export interface ContractStatusOption {
  slug: ResidencyRegistrationStatus;
  displayValue: string;
}

export enum ContractStatusEnum {
  CURRENT = 'current',
  ACTIVE = '30',
  APPLIED = '0',
  CANCELLED = '-10',
  DEPOSIT = '20',
  NOTICE = '40',
  SIGNATURE = '10',
  TERMINATED = '50',
}

export interface ContractStatusFilterOption {
  slug: ContractStatusEnum;
  displayValue: string;
}

// Form Data Types
export interface ColocFormData {
  colocFirstName: string;
  colocLastName: string;
  colocEmail: string;
  colocMobileCountryCode: string;
  colocMobileNumber: string;
  colocLocale: string;
  colocId: string;
  colocBirthday: string;
  colocBirthplace: string;
  securityDepositAmount: string;
  securityDepositAmountPaid: string;
}

export interface RoomFormData {
  houseId: string;
  roomId: string;
  accessCode?: string;
}

export interface DatesFormData {
  moveInDate: Date | null;
  moveOutDate: Date | null;
}

export interface FormState {
  colocData: ColocFormData;
  roomData: RoomFormData;
  datesData: DatesFormData;
}

// House Types
export interface House extends Omit<BaseHouse, 'rooms'> {
  rooms: {
    rooms_count: number;
    available_rooms_count: number;
    rooms: [number, number][]; // [room_number, room_id]
  };
}

export type APIHouse = BaseHouse & {
  rooms: {
    rooms_count: number;
    available_rooms_count: number;
  };
};

// Component Props Types
export interface PickRoomFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (formData: RoomFormData) => void;
  initialData?: RoomFormData;
}

export interface AddColocFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: ColocFormData) => void;
  initialData?: ColocFormData;
}

export interface PickDatesFormProps {
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (formData: DatesFormData) => void;
  isSubmitting?: boolean;
  initialData?: DatesFormData;
}
